import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { House, ListChecks, PlusCircle, ChartBar, ShieldCheck, Users, SignOut, Snowflake, Sparkle, UserSwitch, X, CaretDoubleLeft, CaretDoubleRight, List, Calculator } from "@phosphor-icons/react";
import NotificationBell from "@/components/NotificationBell";
import { Toaster } from "sonner";
import Snowflakes from "@/components/Snowflakes";
import BottomNav from "@/components/BottomNav";
import InstallPrompt from "@/components/InstallPrompt";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useEffect, useState } from "react";
import api from "@/lib/api";

const roleNav = {
  requester: [
    { to: "/", icon: House, label: "Dashboard", testid: "nav-dashboard" },
    { to: "/new", icon: PlusCircle, label: "New Request", testid: "nav-new" },
    { to: "/queue", icon: ListChecks, label: "My Queue", testid: "nav-queue" },
    { to: "/reports", icon: ChartBar, label: "Reports", testid: "nav-reports" },
    { to: "/accounting", icon: Calculator, label: "Accounting", testid: "nav-accounting" },
    { to: "/audit-log", icon: ShieldCheck, label: "Audit Log", testid: "nav-audit-log" },
    { to: "/admin", icon: Users, label: "Settings", testid: "nav-admin" },
  ],
  auditor: [
    { to: "/", icon: House, label: "Dashboard", testid: "nav-dashboard" },
    { to: "/queue", icon: ListChecks, label: "Audit Queue", testid: "nav-queue" },
    { to: "/reports", icon: ChartBar, label: "Reports", testid: "nav-reports" },
    { to: "/accounting", icon: Calculator, label: "Accounting", testid: "nav-accounting" },
    { to: "/audit-log", icon: ShieldCheck, label: "Audit Log", testid: "nav-audit-log" },
  ],
  payer: [
    { to: "/", icon: House, label: "Dashboard", testid: "nav-dashboard" },
    { to: "/queue", icon: ListChecks, label: "Payment Queue", testid: "nav-queue" },
  ],
  approver: [
    { to: "/", icon: House, label: "Dashboard", testid: "nav-dashboard" },
    { to: "/queue", icon: ListChecks, label: "Approval Queue", testid: "nav-queue" },
  ],
  admin: [
    { to: "/", icon: House, label: "Dashboard", testid: "nav-dashboard" },
    { to: "/new", icon: PlusCircle, label: "New Request", testid: "nav-new" },
    { to: "/queue", icon: ListChecks, label: "All Requests", testid: "nav-queue" },
    { to: "/reports", icon: ChartBar, label: "Reports", testid: "nav-reports" },
    { to: "/accounting", icon: Calculator, label: "Accounting", testid: "nav-accounting" },
    { to: "/audit-log", icon: ShieldCheck, label: "Audit Log", testid: "nav-audit-log" },
    { to: "/admin", icon: Users, label: "Admin", testid: "nav-admin" },
  ],
};

export default function AppShell() {
  const { user, logout, demoMode, setDemoMode, demoAvailable, impersonate } = useAuth();
  const nav = useNavigate();
  const items = roleNav[user?.role] || roleNav.requester;
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar_collapsed") === "1");
  // Mobile drawer state (< md). Separate from desktop `collapsed`.
  const [drawerOpen, setDrawerOpen] = useState(false);

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      if (next) localStorage.setItem("sidebar_collapsed", "1");
      else localStorage.removeItem("sidebar_collapsed");
      return next;
    });
  };

  // Close mobile drawer on nav
  useEffect(() => { setDrawerOpen(false); }, [nav]);

  const isImpersonating = !!user?._impersonator;
  const isRealAdmin = user?.role === "admin" || isImpersonating;

  return (
    <div className="min-h-screen relative">
      <Snowflakes count={35} />
      <div className="relative z-10 flex min-h-screen">
        {/* Mobile top bar */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-[150] glass border-b border-white/40 px-3 py-2.5 flex items-center gap-2">
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setDrawerOpen(true)}
            className="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-white/60 shrink-0"
            aria-label="Open menu"
          >
            <List size={20} weight="bold" />
          </button>
          <div className="flex items-center gap-1.5 min-w-0">
            <div className="w-7 h-7 rounded-lg chrome flex items-center justify-center shrink-0">
              <Snowflake size={14} weight="fill" className="text-amber-600" />
            </div>
            <div className="font-heading font-black text-base bg-gradient-to-br from-amber-500 to-slate-500 bg-clip-text text-transparent truncate">PayTrack</div>
          </div>
          <div className="flex-1" />
          <NotificationBell />
          <button
            data-testid="mobile-user-chip"
            onClick={() => nav("/admin")}
            className="w-9 h-9 rounded-full chrome flex items-center justify-center shimmer-border shrink-0"
            aria-label={user?.name}
            title={user?.name}
          >
            <span className="font-heading font-black text-xs text-amber-700">
              {(user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
            </span>
          </button>
          <button
            data-testid="mobile-signout"
            onClick={logout}
            className="w-9 h-9 rounded-xl flex items-center justify-center hover:bg-red-50 text-slate-600 hover:text-red-700 shrink-0"
            aria-label="Sign out"
            title="Sign out"
          >
            <SignOut size={16} weight="bold" />
          </button>
        </div>

        {/* Drawer overlay on mobile */}
        {drawerOpen && (
          <div
            data-testid="mobile-drawer-overlay"
            onClick={() => setDrawerOpen(false)}
            className="md:hidden fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm"
          />
        )}

        {/* Sidebar — hidden on <md unless drawerOpen */}
        <aside
          data-testid="sidebar"
          data-collapsed={collapsed ? "true" : "false"}
          className={`${collapsed ? "md:w-[76px]" : "md:w-60"} !fixed md:!static top-0 bottom-0 left-0 z-50 md:z-auto w-64 m-0 md:m-4 md:mr-0 flex flex-col rounded-none md:rounded-2xl glass shimmer-border overflow-hidden transition-[width,transform] duration-200 ease-out ${drawerOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}
        >
          <div className={`p-5 border-b border-white/40 flex ${collapsed ? "flex-col items-center gap-2" : "items-center gap-3"}`}>
            <div className="w-9 h-9 rounded-xl chrome flex items-center justify-center flex-shrink-0">
              <Snowflake size={18} weight="fill" className="text-amber-600" />
            </div>
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <div className="font-heading font-black text-lg leading-none bg-gradient-to-br from-amber-500 to-slate-500 bg-clip-text text-transparent truncate">PayTrack</div>
                <div className="overline mt-1">Payment ops</div>
              </div>
            )}
            <button
              data-testid="sidebar-collapse-toggle"
              onClick={toggleCollapsed}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-600 hover:text-slate-900 hover:bg-white/70 transition-all flex-shrink-0"
            >
              {collapsed ? <CaretDoubleRight size={14} weight="bold" /> : <CaretDoubleLeft size={14} weight="bold" />}
            </button>
          </div>
          <nav className="flex-1 p-3 space-y-1">
            {items.map(({ to, icon: Icon, label, testid }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                data-testid={testid}
                title={collapsed ? label : undefined}
                className={({ isActive }) =>
                  `group flex items-center ${collapsed ? "justify-center px-0" : "gap-3 px-3"} py-2.5 text-sm rounded-xl transition-all ${
                    isActive ? "amber-pill font-semibold" : "text-slate-800 hover:bg-white/60"
                  }`
                }
              >
                <Icon size={18} weight="duotone" />
                {!collapsed && <span className="truncate">{label}</span>}
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* Main area with top bar */}
        <div className="flex-1 min-w-0 flex flex-col pt-14 md:pt-0">
          <TopBar
            user={user}
            isRealAdmin={isRealAdmin}
            isImpersonating={isImpersonating}
            demoMode={demoMode}
            setDemoMode={setDemoMode}
            demoAvailable={demoAvailable}
            impersonate={impersonate}
            onLogout={async () => { await logout(); nav("/login"); }}
          />
          <main className="flex-1 min-w-0 overflow-auto pb-24 md:pb-0">
            <Outlet />
          </main>
        </div>
      </div>
      <BottomNav role={user?.role} />
      <InstallPrompt />
      <Toaster richColors position="top-right" closeButton />
    </div>
  );
}

function TopBar({ user, isRealAdmin, isImpersonating, demoMode, setDemoMode, demoAvailable, impersonate, onLogout }) {
  const [impersonateOptions, setImpersonateOptions] = useState([]);

  useEffect(() => {
    if (demoMode && isRealAdmin) {
      api.get("/admin/demo/impersonate-list").then((r) => setImpersonateOptions(r.data)).catch(() => setImpersonateOptions([]));
    } else {
      setImpersonateOptions([]);
    }
  }, [demoMode, isRealAdmin]);

  return (
    <div className="hidden md:flex relative z-[150] mx-3 md:mx-4 mt-3 md:mt-4 mb-2 flex-wrap items-center gap-2 md:gap-3 glass rounded-2xl px-3 md:px-4 py-2.5">
      {/* Demo mode toggle — admin only, requires demo data */}
      {isRealAdmin && demoAvailable && (
        <div className="flex items-center gap-2" data-testid="demo-toggle-wrap">
          <Sparkle size={16} weight="fill" className="text-amber-600" />
          <span className="text-xs font-semibold tracking-wide text-slate-700">DEMO MODE</span>
          <Switch data-testid="demo-toggle" checked={demoMode} onCheckedChange={(v) => { setDemoMode(v); if (!v) impersonate(null); }} />
          <span className={`text-[11px] font-mono px-2 py-0.5 rounded-full ${demoMode ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
            {demoMode ? "SANDBOX" : "LIVE"}
          </span>
        </div>
      )}

      {/* Impersonation selector — visible in demo mode only, admin only */}
      {demoMode && isRealAdmin && impersonateOptions.length > 0 && (
        <div className="flex items-center gap-2 pl-3 ml-1 border-l border-white/60" data-testid="impersonate-wrap">
          <UserSwitch size={16} weight="duotone" className="text-slate-600" />
          <Select value={isImpersonating ? user.id : "self"} onValueChange={(v) => impersonate(v === "self" ? null : v)}>
            <SelectTrigger data-testid="impersonate-select" className="h-9 rounded-lg bg-white/70 border-white/60 min-w-[220px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="self">View as admin (me)</SelectItem>
              {impersonateOptions.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  View as {u.role} · {u.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isImpersonating && (
            <button data-testid="exit-impersonation" onClick={() => impersonate(null)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:underline">
              <X size={12} weight="bold" /> Exit
            </button>
          )}
        </div>
      )}

      {/* Impersonation banner when active */}
      {isImpersonating && (
        <div data-testid="impersonate-banner" className="ml-auto flex items-center gap-2 px-3 py-1 rounded-lg bg-amber-100 border border-amber-300 text-xs font-semibold text-amber-900">
          👁 Viewing as <span className="uppercase">{user.role}</span> ({user.email}) — you are really {user._impersonator?.email}
        </div>
      )}

      {/* Right side: notification bell + user chip + sign out */}
      <div className={`${isImpersonating ? "" : "ml-auto"} flex items-center gap-3`}>
        <NotificationBell />
        <div className="text-right hidden md:block">
          <div className="text-xs font-semibold text-slate-800" data-testid="topbar-name">{user?.name}</div>
          <div className="text-[10px] font-mono text-slate-500 uppercase tracking-wide" data-testid="user-email">{user?.role}</div>
        </div>
        <div className="w-9 h-9 rounded-full chrome flex items-center justify-center font-heading font-black text-slate-700 text-sm">
          {(user?.name || "?")[0].toUpperCase()}
        </div>
        <button
          data-testid="logout-btn"
          onClick={onLogout}
          className="btn-ghost rounded-xl h-9 px-3 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-800 hover:text-red-600"
        >
          <SignOut size={14} weight="bold" /> Sign out
        </button>
      </div>
    </div>
  );
}
